/**
 * Station queue — the tablet worker's "what's pending here" list. One clean card
 * per in-flight order at this station, most-urgent first (nearest ex-factory,
 * then most pending). The balance is a big mono number — the one thing the
 * worker acts on — with everything else kept quiet (hairline card, one sky
 * accent on the progress bar). Tapping a card opens the record sheet. Reads
 * `wip.queue`.
 */
import { ChevronRight } from "lucide-react";
import { Alert, cn } from "@flint/ui";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { stationByKey } from "../../../lib/garment-stations.js";

type WipStage = wipContract.WipStage;
export interface QueueRow {
  styleId: string;
  styleRef: string;
  name: string;
  buyerName: string;
  exFactoryDate: string | null;
  received: number;
  output: number;
  loss: number;
  balance: number;
}

type Urgency = "overdue" | "soon" | "normal";

export function StationQueue({
  stage,
  onSelect,
}: {
  stage: WipStage;
  onSelect: (row: QueueRow) => void;
}) {
  const q = trpc.wip.queue.useQuery({ stage });
  const station = stationByKey(stage);

  if (q.isLoading) {
    return <p className="p-10 text-center text-muted">Loading your queue…</p>;
  }
  if (q.error) {
    return (
      <div className="p-4">
        <Alert intent="bad" title="Couldn't load the queue" description={q.error.message} />
      </div>
    );
  }

  const rows = [...(q.data?.rows ?? [])].sort(byUrgency);
  const pending = rows.filter((r) => r.balance > 0);
  const cleared = rows.filter((r) => r.balance <= 0);

  if (rows.length === 0) {
    return (
      <div className="grid place-items-center px-4 py-24 text-center">
        <div className="text-lg font-semibold text-ink">Nothing waiting here</div>
        <p className="mt-1 text-sm text-muted">
          No orders are at {station?.label ?? "this station"} right now.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 pb-12">
      {pending.length === 0 ? (
        <div className="rounded-2xl border border-ok-100 bg-ok-50 px-5 py-4 text-center text-sm font-semibold text-ok">
          All caught up — nothing pending at {station?.label ?? "this station"}.
        </div>
      ) : (
        pending.map((r) => <TicketCard key={r.styleId} row={r} onSelect={onSelect} />)
      )}

      {cleared.length > 0 ? (
        <>
          <div className="mt-4 px-1 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
            Done here · {cleared.length}
          </div>
          {cleared.map((r) => (
            <ClearedRow key={r.styleId} row={r} />
          ))}
        </>
      ) : null}
    </div>
  );
}

function TicketCard({ row, onSelect }: { row: QueueRow; onSelect: (row: QueueRow) => void }) {
  const done = row.received > 0 ? Math.min(100, Math.round((row.output / row.received) * 100)) : 0;
  const urgency = urgencyOf(row.exFactoryDate);
  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className="group w-full rounded-2xl border border-line bg-paper p-5 text-left shadow-card transition hover:border-line-2 active:scale-[0.995]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-base font-semibold tracking-tight text-ink">{row.styleRef}</span>
            {urgency !== "normal" && row.exFactoryDate ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-semibold",
                  urgency === "overdue" ? "bg-bad-50 text-bad" : "bg-warn-50 text-warn",
                )}
              >
                {urgency === "overdue" ? "Overdue" : "Due"} {formatDate(row.exFactoryDate)}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 truncate text-sm text-muted">
            {row.name} · {row.buyerName}
          </div>
        </div>
        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-line-2 transition group-hover:text-muted" />
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-4xl font-bold tabular-nums tracking-tight text-ink">
            {row.balance.toLocaleString("en-IN")}
          </span>
          <span className="text-sm text-muted">pcs pending</span>
        </div>
        <span className="font-mono text-xs tabular-nums text-muted">
          {row.output.toLocaleString("en-IN")} / {row.received.toLocaleString("en-IN")}
        </span>
      </div>

      <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-raised">
        <div className="h-full rounded-full bg-sky-600" style={{ width: `${done}%` }} />
      </div>
    </button>
  );
}

function ClearedRow({ row }: { row: QueueRow }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-paper px-4 py-3">
      <span className="font-mono text-sm font-semibold text-ink-2">{row.styleRef}</span>
      <span className="truncate text-sm text-muted">{row.name}</span>
      <span className="ml-auto text-xs font-semibold uppercase tracking-wide text-ok">Cleared</span>
    </div>
  );
}

/** Nearest ex-factory first (nulls last), then most pending. */
function byUrgency(a: QueueRow, b: QueueRow): number {
  const ad = a.exFactoryDate ?? "9999-12-31";
  const bd = b.exFactoryDate ?? "9999-12-31";
  if (ad !== bd) return ad < bd ? -1 : 1;
  return b.balance - a.balance;
}

/** overdue (past today) · soon (within 7 days) · normal — drives the due chip. */
function urgencyOf(exFactoryDate: string | null): Urgency {
  if (!exFactoryDate) return "normal";
  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  if (exFactoryDate < today) return "overdue";
  if (exFactoryDate <= in7) return "soon";
  return "normal";
}
